import { iot, mqtt5 } from 'aws-iot-device-sdk-v2'
import { once } from 'events'

// TYPES
import type { Topic, TopicCallback, TopicListener } from './types'

// UTILITIES
import { validateEnvVar } from '@utils/temp-helpers'
import { isNonEmptyString, isObject } from '@utils/type-checkers'


export class IoTClient {
    // Config
    private static readonly hostname = validateEnvVar('VITE_IOT_CLIENT_HOSTNAME')
    private static readonly baseConfig: iot.MqttConnectCustomAuthConfig = {
        authorizerName: validateEnvVar('VITE_IOT_AUTHORIZER_NAME'),
        password: Buffer.from(validateEnvVar('VITE_IOT_PASSWORD'), 'base64'),
        tokenValue: validateEnvVar('VITE_IOT_TOKEN_VALUE'),
        tokenSignature: validateEnvVar('VITE_IOT_TOKEN_VALUE_SIG'),
        tokenKeyName: encodeURIComponent('Token-Header'),
    }
    private username: string

    readonly client: mqtt5.Mqtt5Client
    private topics: Partial<Record<Topic, Record<string, TopicCallback>>> = {}
    private clientState: 'connecting' | 'connected' | 'closed' = 'closed'

    private static instance: IoTClient

    private constructor(username: string) {
        this.username = username
        this.client = new mqtt5.Mqtt5Client(iot.AwsIotMqtt5ClientConfigBuilder
            .newWebsocketMqttBuilderWithCustomAuth(IoTClient.hostname, {
                ...IoTClient.baseConfig,
                username: this.username,
            })
            .withSessionBehavior(mqtt5.ClientSessionBehavior.Clean)
            .build()
        )

        this.client.on('error', (error) => { console.error('Error Event:', error) })
        this.client.on('messageReceived', (event): void => {
            console.info("Message Received:", event.message)
            const payload = event.message.payload
            const topic = event.message.topicName
            if (payload) {
                const jsonString = Buffer.from(payload as Uint8Array).toString('utf8')
                const parsedData = JSON.parse(jsonString)
                const callbacks = this.topics[`/${topic}/` as Topic]
                if (!!callbacks) Object.values(callbacks).forEach((cb) => cb(parsedData.Item))
            }
        })

        // Lifecycle
        this.client.on('attemptingConnect', () => { this.clientState = 'connecting' })
        this.client.on('connectionSuccess', (eventData) => {
            this.clientState = 'connected'
            const topics = Object.keys(this.topics)
            if (!!topics.length) topics.forEach((topic) => { this.openChannel(topic as Topic) })
            console.group('Connection Success Event')
            console.info('Connack:', eventData.connack)
            console.info('Settings:', eventData.settings)
            console.groupEnd()
        })
        this.client.on('connectionFailure', (eventData) => {
            console.group('Connection Failure Event')
            console.info('Error:', eventData.error)
            console.info('Connack:', eventData.connack)
            console.groupEnd()
        })
        this.client.on('disconnection', (eventData) => {
            this.clientState = 'closed'
            console.group('Disconnection Event')
            console.info('Message:', eventData.error)
            console.info('Packet:', eventData.disconnect)
            console.groupEnd()
        })
        this.client.on('stopped', () => { this.clientState = 'closed' })

        window.addEventListener('offline', () => {
            this.clientState = 'closed'
        })

        this.openConnection()
    }

    static getInstance(username?: string) {
        if (!isNonEmptyString(username)) {
            if (IoTClient.instance) return IoTClient.instance
            else throw new Error('No IoTClient instance exists. Initiate the getInstance method with a username to create a new singleton.')
        } else {
            if (IoTClient.instance && username === IoTClient.instance.username) return IoTClient.instance
            else {
                if (IoTClient.instance) IoTClient.instance.closeConnection();
                this.instance = new IoTClient(username)
                return this.instance
            }
        }
    }

    addTopicListener = (async (t, callback, settings) => {
        var topic = t
        if (isObject(settings) && 'wildcard' in settings) {
            const wildcard = settings.wildcard || ''
            topic = topic.replace('*', wildcard) as Topic
        }

        console.log("Adding Topic Listener", topic.slice(1, -1), this.clientState)
        if (this.clientState === 'connected') {
            if (!!this.topics[topic]) this.topics[topic];
            else if (this.clientState === 'connected') this.openChannel(topic).then(() => {
                this.topics[topic] = { [callback.toString()]: callback }
            })
        } else {
            if (!isObject(this.topics[topic])) this.topics[topic] = {}
            this.topics[topic]![callback.toString()] = callback
        }
    }) satisfies TopicListener

    removeTopicListener = ((t, callback, settings) => {
        var topic = t
        if (isObject(settings) && 'wildcard' in settings) {
            const wildcard = settings.wildcard || ''
            topic = topic.replace('*', wildcard) as Topic
        }

        if (isObject(this.topics[topic])) {
            console.log("Removing Topic Listener", topic.slice(1, -1))
            const key = callback.toString()
            if (this.clientState === 'connected') {
                if (!!this.topics[topic]![key]) delete this.topics[topic]![key]
                if (!Object.values(this.topics[topic]!).length) this.closeChannel(topic).then(() => {
                    delete this.topics[topic]
                })
            } else {
                if (!!this.topics[topic]![key]) delete this.topics[topic]![key]
                if (!Object.values(this.topics[topic]!).length) delete this.topics[topic]
            }
        }
    }) satisfies TopicListener

    async publishMessage(topic: string, payload: mqtt5.Payload, options?: Partial<Omit<mqtt5.PublishPacket, 'topicName' | 'payload'>>) {
        const publishPromise = this.client.publish({ topicName: (topic as string).slice(1, -1), qos: mqtt5.QoS.AtLeastOnce, payload, ...options })
            .then((response) => {
                const { reasonCode, reasonString } = response || {}
                console.info("Publish Response:", response)
                switch (reasonCode) {
                    case mqtt5.PubackReasonCode.Success: break;
                    case mqtt5.PubackReasonCode.NoMatchingSubscribers: break;
                    case mqtt5.PubackReasonCode.UnspecifiedError: break;
                    case mqtt5.PubackReasonCode.ImplementationSpecificError: break;
                    case mqtt5.PubackReasonCode.NotAuthorized: break;
                    case mqtt5.PubackReasonCode.TopicNameInvalid: break;
                    case mqtt5.PubackReasonCode.PacketIdentifierInUse: break;
                    case mqtt5.PubackReasonCode.QuotaExceeded: break;
                    case mqtt5.PubackReasonCode.PayloadFormatInvalid: break;
                    default: throw Error(`[${reasonCode}] Unsupported Code: ${reasonString}`)
                }
            })

        const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Publish timed out')), 5000)
        )

        return Promise.race([publishPromise, timeoutPromise])
            .catch((err) => {
                console.error(err)
                throw err
            })
    }

    private async openChannel(topic: Topic, options?: Omit<mqtt5.Subscription, 'topicFilter'>) {
        await this.client.subscribe({ subscriptions: [{ ...options, qos: options?.qos || mqtt5.QoS.AtLeastOnce, topicFilter: `${topic.slice(1, -1)}` }] })
            .then((results) => { console.info('Topic Channel Open:', { [topic.slice(1, -1)]: results }) })
            .catch((error) => { console.error(`Failed to subscribe to topic: ${topic}`, error) })
    }

    private async closeChannel(topic: Topic) {
        await this.client.unsubscribe({ topicFilters: [`${topic.slice(1, -1)}`] })
            .then((results) => { console.info('Topic Channel Closed:', { [topic.slice(1, -1)]: results }) })
            .catch((error) => { console.error(`Failed to subscribe to topic: ${topic.slice(1, -1)}`, error) })
    }

    async ensureConnection() {
        if (this.clientState !== 'connected' || !navigator.onLine) {
            console.log('Ensuring connection...')
            if (!navigator.onLine) throw new Error('No internet connection')
            await this.openConnection()
        }
    }

    private async openConnection() {
        this.client.stop()
        await new Promise(resolve => setTimeout(resolve, 500))

        const connectionSuccess = once(this.client, 'connectionSuccess')
        this.client.start()
        await connectionSuccess
    }

    private async closeConnection() {
        const disconnection = once(this.client, 'disconnection')
        const stopped = once(this.client, 'stopped')
        this.client.stop()
        await disconnection
        await stopped
    }
}